/**
 * How wide the left thread sidebar may be.
 *
 * The maximum is derived from the window rather than stated, because the bound
 * that matters is the *content column's* minimum, not the sidebar's own size.
 * Writing it as a constant means picking one that is wrong at every width
 * except the one it was measured on — which is how the right panel ended up
 * with a 720px ceiling a 1900px monitor could not talk it out of.
 *
 * Stated in px, deliberately. Forge's root font-size is user-adjustable from
 * 11px to 20px, so a `rem` bound here would drift against the drag clamp, which
 * only ever sees pixels. `SIDEBAR_WIDTH` in `ui/sidebar.tsx` is still `16rem`;
 * at the 11px setting that opens the sidebar at 176px, below the 208px floor
 * below — which is why the mount path resolves an explicit initial width
 * instead of letting the stylesheet's default stand.
 */
export const THREAD_SIDEBAR_WIDTH_STORAGE_KEY = "chat_thread_sidebar_width";
export const THREAD_SIDEBAR_DEFAULT_WIDTH = 16 * 16;
export const THREAD_SIDEBAR_MIN_WIDTH = 13 * 16;
/** What the transcript + right panel together must keep between them. */
export const THREAD_MAIN_CONTENT_MIN_WIDTH = 40 * 16;

/**
 * The sidebar yields to the content column, but never past its own floor.
 *
 * Clamping to the minimum on a window too narrow to satisfy both is not a
 * rounding detail: `clampSidebarWidth` resolves an inverted range to the
 * *maximum*, so returning a negative here would pin the sidebar to a sliver
 * and then persist it at drag-end.
 */
export function resolveThreadSidebarMaximumWidth(viewportWidth: number): number {
  return Math.max(
    THREAD_SIDEBAR_MIN_WIDTH,
    Math.floor(viewportWidth) - THREAD_MAIN_CONTENT_MIN_WIDTH,
  );
}

/**
 * The width to open at, given what was stored and how wide the window is now.
 *
 * A width stored on an external monitor has to survive being reopened on a
 * laptop, so the stored value is a preference that gets clamped on read rather
 * than a fact that gets applied. Storage keeps the larger number, so the
 * sidebar returns to it when the window grows back.
 */
export function resolveInitialThreadSidebarWidth(
  storedWidth: number | null,
  viewportWidth: number,
): number {
  const preferredWidth =
    storedWidth === null
      ? THREAD_SIDEBAR_DEFAULT_WIDTH
      : Math.max(THREAD_SIDEBAR_MIN_WIDTH, storedWidth);
  return Math.min(preferredWidth, resolveThreadSidebarMaximumWidth(viewportWidth));
}
